import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import OktaAuth from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { Outlet } from '@rocketlane/rocket-router';
import React, { memo, Suspense, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { AccountAPI } from 'api/account';
import { PageLoading } from 'components/atoms/loading';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { CONFIG } from 'config/okta';
import { RoutePaths } from 'constant';
import { setQueryClientAction } from 'data/core/actions';
import { queryClient, useAccountMetaQuery } from 'queries';
import { MobileViewOverlay } from 'routes/mobile-view-overlay';
import { getOkta, logger, setOkta } from 'services';
import { ApiKey } from 'services/api-key';
import { authService } from 'services/auth-service';
import { isTauriApp } from 'utils/is-tauri';
// Keep TokenErrorStatus as regular import since it's an enum/type
const AccountNotFound = /*#__PURE__*/ React.lazy(()=>import('components/organisms/account').then((d)=>({
            default: d.AccountNotFound
        })));
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
// Survives soft HMR re-eval so a hot update does not flash PageLoading again.
let authSetupLatchComplete = false;
/** @internal test seam */ export const __resetAuthSetupLatchForTests = ()=>{
    authSetupLatchComplete = false;
};
const isAuthAlreadyReady = ()=>authSetupLatchComplete || getOkta() !== null || isTauriApp() && ApiKey.exists();
export const SetupOkta = /*#__PURE__*/ memo(()=>{
    const dispatch = useDispatch();
    const { pathname } = window.location;
    const [authSetupComplete, setAuthSetupComplete] = useState(isAuthAlreadyReady);
    useEffect(()=>{
        dispatch(setQueryClientAction(queryClient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        queryClient
    ]);
    const { data, isLoading, isError, error } = useAccountMetaQuery();
    // Drive completion from settled query state — not only from onSuccess.
    // After HMR remounts, account-meta is often still cached (staleTime: oneDay),
    // so TanStack Query v4 does not re-fire onSuccess and authSetupComplete would
    // stay false forever on PageLoading.
    useEffect(()=>{
        if (authSetupComplete || isLoading) {
            return undefined;
        }
        if (isError) {
            logger.error('Account meta failed', {
                error: error
            });
            authSetupLatchComplete = true;
            setAuthSetupComplete(true);
            return undefined;
        }
        if (!data) {
            return undefined;
        }
        let cancelled = false;
        const complete = ()=>{
            if (!cancelled) {
                authSetupLatchComplete = true;
                setAuthSetupComplete(true);
            }
        };
        const run = async ()=>{
            // Guard: only create the OktaAuth instance once. In React 18 StrictMode
            // effects (and their cleanups) run twice, which would create a fresh
            // OktaAuth — passing a new reference to <Security> re-initializes it,
            // resetting authState and unmounting the tree.
            if (getOkta() !== null) {
                complete();
                return;
            }
            // Desktop API-key sessions must not initialize Okta — Okta's
            // authState.isAuthenticated===false would trigger OAuth hard-redirects
            // (UnauthenticatedRedirector / login.tsx) and flicker in a loop.
            if (isTauriApp() && ApiKey.exists()) {
                authService.setAuthState(true);
                complete();
                return;
            }
            const { clientId, issuerDomain, cookiesEnabled } = data.okta;
            // Probe the cookie session first so authState is true before <Security> initializes.
            // Skip on the callback page: no valid cookie exists yet there.
            const isCallbackPage = window.location.pathname.includes('/implicit/callback');
            if (cookiesEnabled && !isCallbackPage) {
                try {
                    await AccountAPI.checkSession();
                    if (!cancelled) {
                        authService.setAuthState(true);
                    }
                } catch (unused) {
                // Session invalid or network error — auth state stays false.
                }
            }
            if (cancelled) {
                return;
            }
            if (clientId && issuerDomain) {
                const oktaConfig = CONFIG(issuerDomain, clientId);
                const authInstance = new OktaAuth(oktaConfig);
                setOkta(authInstance);
                authInstance.authStateManager.updateAuthState();
            }
            complete();
        };
        void run();
        return ()=>{
            cancelled = true;
        };
    }, [
        authSetupComplete,
        data,
        isLoading,
        isError,
        error
    ]);
    const oktaAuth = getOkta();
    const isAccountActionPage = pathname === RoutePaths.activate;
    // Stable callback — prevents Security from re-registering on every render
    const stableRestoreOriginalUri = useCallback(()=>{}, []);
    const componentToRender = ()=>{
        if (isLoading || !authSetupComplete) {
            return /*#__PURE__*/ _jsx(PageLoading, {});
        }
        if (data) {
            var _data_company, _data_company1, _data_company2, _data_company3;
            const appContent = /*#__PURE__*/ _jsxs(Suspense, {
                fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
                children: [
                    /*#__PURE__*/ _jsxs(Helmet, {
                        titleTemplate: `%s - ${(_data_company = data.company) === null || _data_company === void 0 ? void 0 : _data_company.companyName}`,
                        defaultTitle: (_data_company1 = data.company) === null || _data_company1 === void 0 ? void 0 : _data_company1.companyName,
                        children: [
                            /*#__PURE__*/ _jsx("link", {
                                rel: "icon",
                                type: "image/png",
                                href: (_data_company2 = data.company) === null || _data_company2 === void 0 ? void 0 : _data_company2.companyLogoUrl
                            }),
                            /*#__PURE__*/ _jsx("link", {
                                rel: "icon",
                                type: "image/png",
                                sizes: "16x16",
                                href: (_data_company3 = data.company) === null || _data_company3 === void 0 ? void 0 : _data_company3.companyLogoUrl
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Outlet, {})
                ]
            });
            if (oktaAuth && !(isTauriApp() && ApiKey.exists())) {
                return /*#__PURE__*/ _jsx(Security, {
                    oktaAuth: oktaAuth,
                    restoreOriginalUri: stableRestoreOriginalUri,
                    children: appContent
                });
            }
            // Cookies flow / desktop API-key: no Okta Security wrapper
            return appContent;
        }
        if (isAccountActionPage) {
            return /*#__PURE__*/ _jsx(Suspense, {
                fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
                children: /*#__PURE__*/ _jsx(TokenError, {
                    status: TokenErrorStatus.AlreadyCompleted
                })
            });
        }
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(AccountNotFound, {})
        });
    };
    return /*#__PURE__*/ _jsx(MobileViewOverlay, {
        children: componentToRender()
    });
});
SetupOkta.displayName = 'SetupOkta';
